import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRole } from '@/context/RoleContext';
import { searchFeatures } from '@/utils/featureSearch';
import { searchPortal, type SearchGroup, type SearchResult } from '@/utils/search';

const groupOrder: SearchGroup[] = ['Go to', 'Campaigns', 'Creators', 'Invoices'];

export default function GlobalSearch() {
  const navigate = useNavigate();
  const { can } = useRole();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  // Below md the box is hidden behind a search button; opening it covers the top bar.
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const [found, setFound] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  // Waits for a pause in typing, and drops replies to earlier queries.
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setFound([]);
      setSearching(false);
      setSearchError('');
      return;
    }
    const controller = new AbortController();
    setSearching(true);
    const timer = window.setTimeout(() => {
      searchPortal(trimmed, controller.signal)
        .then((matches) => {
          setFound(matches);
          setSearchError('');
        })
        .catch((error: Error) => {
          if (controller.signal.aborted) return;
          setFound([]);
          setSearchError(error.message);
        })
        .finally(() => {
          if (!controller.signal.aborted) setSearching(false);
        });
    }, 250);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  // Pages and actions match at once, in the browser; records come from the API.
  const results = useMemo(
    () => (query.trim().length < 2 ? [] : [...searchFeatures(query, can), ...found]),
    [query, can, found],
  );

  const grouped = useMemo(
    () =>
      groupOrder
        .map((group) => ({
          group,
          items: results.filter((result) => result.group === group),
        }))
        .filter((section) => section.items.length > 0),
    [results],
  );

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) inputRef.current?.focus();
  }, [mobileOpen]);

  const close = () => {
    setOpen(false);
    setMobileOpen(false);
  };

  const select = (result: SearchResult) => {
    close();
    setQuery('');
    navigate(result.path);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      close();
      return;
    }
    if (!open || results.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + results.length) % results.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      select(results[activeIndex]);
    }
  };

  const showDropdown = open && query.trim().length > 0;
  let flatIndex = -1;

  return (
    <>
    <button
      type="button"
      onClick={() => setMobileOpen(true)}
      aria-label="Search"
      className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-200 cursor-pointer md:hidden"
    >
      <i className="ri-search-line text-lg" />
    </button>
    <div
      ref={containerRef}
      className={`${
        mobileOpen ? 'absolute inset-x-0 bottom-0 top-[3px] z-40 flex items-center gap-2 bg-background-50 px-4' : 'hidden'
      } md:relative md:inset-auto md:z-auto md:block md:bg-transparent md:px-0`}
    >
      <div className="flex flex-1 items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 md:w-80 md:flex-none">
        <i className="ri-search-line text-foreground-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search campaigns, creators, invoices..."
          className="h-9 w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
        />
        {query ? (
          <button
            type="button"
            onClick={() => {
              setQuery('');
              setOpen(false);
            }}
            aria-label="Clear search"
            className="w-4 h-4 flex items-center justify-center text-foreground-400 hover:text-foreground-700 cursor-pointer"
          >
            <i className="ri-close-line" />
          </button>
        ) : null}
      </div>
      <button
        type="button"
        onClick={close}
        className="shrink-0 px-1 text-sm font-medium text-foreground-700 cursor-pointer md:hidden"
      >
        Cancel
      </button>

      {showDropdown ? (
        <div className="absolute inset-x-4 top-full z-50 mt-1 max-h-[70vh] overflow-y-auto rounded-lg border border-background-200 bg-background-50 p-2 md:inset-x-auto md:left-0 md:top-auto md:mt-2 md:w-96">
          {query.trim().length < 2 ? (
            <p className="px-2 py-4 text-center text-xs text-foreground-500">
              Keep typing — at least 2 characters.
            </p>
          ) : searching && results.length === 0 && !searchError ? (
            <p className="px-2 py-4 text-center text-xs text-foreground-500">
              <i className="ri-loader-4-line mr-1 inline-block animate-spin" />
              Searching…
            </p>
          ) : searchError && results.length === 0 ? (
            <p role="alert" className="px-2 py-4 text-center text-xs text-primary-700">
              <i className="ri-error-warning-line mr-1" />
              Search failed: {searchError}
            </p>
          ) : results.length === 0 ? (
            <div className="px-2 py-6 text-center">
              <div className="mx-auto w-9 h-9 flex items-center justify-center rounded-md bg-background-100 text-foreground-400">
                <i className="ri-search-line text-lg" />
              </div>
              <p className="mt-2 text-xs text-foreground-500">
                No matches for “{query.trim()}”
              </p>
              <p className="mt-0.5 text-[11px] text-foreground-400">
                Try a campaign, creator or invoice name, or what you want to do (e.g. pay, invite, report).
              </p>
            </div>
          ) : (
            grouped.map((section) => (
              <div key={section.group} className="mb-1 last:mb-0">
                <p className="px-2 py-1 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
                  {section.group}
                </p>
                {section.items.map((item) => {
                  flatIndex += 1;
                  const itemIndex = flatIndex;
                  const isActive = itemIndex === activeIndex;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onMouseEnter={() => setActiveIndex(itemIndex)}
                      onClick={() => select(item)}
                      className={`flex w-full items-center gap-2 rounded-md px-2 py-2 text-left cursor-pointer ${
                        isActive ? 'bg-background-100' : 'hover:bg-background-100'
                      }`}
                    >
                      <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-200 text-foreground-600 shrink-0">
                        <i className={item.icon} />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-xs font-medium text-foreground-900">
                          {item.title}
                        </span>
                        <span className="block truncate text-[11px] text-foreground-500">
                          {item.subtitle}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
          {results.length > 0 && searching ? (
            <p className="px-2 py-1.5 text-[11px] text-foreground-400">
              <i className="ri-loader-4-line mr-1 inline-block animate-spin" />
              Searching campaigns, creators and invoices…
            </p>
          ) : results.length > 0 && searchError ? (
            <p role="alert" className="px-2 py-1.5 text-[11px] text-primary-700">Record search failed: {searchError}</p>
          ) : null}
        </div>
      ) : null}
    </div>
    </>
  );
}